import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type ReportAccessGrant } from '@webapp/platform/iam/entities/ReportAccessGrant'
import messages from './messages'
import { useMutateRevokeReportAccessGrant } from '../../models/useMutateRevokeReportAccessGrant'

export interface IUseRevokeReportAccessGrantPresenterOptions {
  /**
   * Fired when a revoke fails and the grant is restored to the list —
   * the Feature Set re-highlights the restored tag (BHV-018).
   */
  onRevokeError?: (grant: ReportAccessGrant) => void
}

export const useRevokeReportAccessGrantPresenter = ({
  onRevokeError,
}: IUseRevokeReportAccessGrantPresenterOptions = {}) => {
  const intl = useProcurifyIntl()
  const { revokeReportAccessGrant, isRevoking } =
    useMutateRevokeReportAccessGrant()

  /**
   * Optimistically removes the tag then DELETEs the grant — no confirmation
   * (BHV-017). On error the Model restores the grant to the top of the list;
   * this hook signals the Feature Set and shows the error toast (BHV-018).
   */
  const revoke = async (
    grant: ReportAccessGrant,
    secondaryCategoryId: number
  ): Promise<void> => {
    try {
      await revokeReportAccessGrant({ grant, secondaryCategoryId })
    } catch (err) {
      onRevokeError?.(grant)

      toast.error({
        title: intl.formatMessage(messages.errorToastTitle),
        children:
          getMessageFromError(err) ??
          intl.formatMessage(messages.errorToastDefaultMessage),
      })
    }
  }

  return {
    commands: {
      revoke,
    },
    states: {
      isRevoking,
    },
  }
}
